import {
  ALL_LAYOUT_ARGS,
  Arg,
  BUTTON_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  LINK_ARGS,
  Metadata,
  POSITIONING_ARGS,
  pick,
  pickAll,
} from '../../../utils/spds/metadata';
import { BUTTON_SIZES, NORMAL } from '../../../utils/spds/constants/sizes';
import { CENTER } from '../../../utils/spds/constants/css-layout';
import { Debug } from '../../../utils/spds/debug';
import { HTML_BUTTON_TYPES, TYPE_BUTTON } from '../../../utils/spds/constants/buttons';
import { LinkTo } from '@ember/routing';
import { ROLE_BUTTON } from '../../../utils/spds/constants/aria';
import { doesNotExist, isActive } from '../../../utils/spds/component-validations/helpers';
import { runInDebug } from '@ember/debug';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Container from './container.gjs';
import SpdsAutofocus from '../../../modifiers/spds-autofocus';
import Super from './base.gjs';
import styles from './button.module.scss';

const ANCHOR_TAG = 'a';

// An undefined attribute value removes the attribute rather than emitting it empty, which is what
// keeps the anchor unfocusable and unexposed as a link.
const NO_HREF = undefined;

/**
 * Anchor for a route-aware button that deliberately has no `href` — a disabled link, or one whose
 * model cannot produce a URL. Both skip `LinkTo`, so they also lose the attribute ordering that
 * makes `LinkTo` immune to a splatted `href`: `Container` applies `...attributes` last, so a
 * consumer-supplied `href` would land on the anchor and restore the navigation these paths exist
 * to prevent. Re-applying `href` after the splat reproduces `LinkTo`'s own ordering.
 */
const UnnavigableLink = <template>
  <a ...attributes href={{NO_HREF}}>{{yield}}</a>
</template>;

const METADATA = Metadata.define({
  description: `
    Base button component for SPDS with flexible layout options and styling

    ### Customization

    The HTML tag for the component can be customized by providing the \`@tag\` argument. In the case that this is set to something other
    than \`button\`, the component will automatically apply the HTML attribute \`role\` with the value \`button\` to the generated HTML
    element.

    This button supports all of our standard button CSS classes (primary, secondary, ghost, etc).

    ### Routing

    Providing \`@route\` (or \`@query\` alone, to link to the current route) renders the button as an
    Ember \`LinkTo\`. The element becomes an \`a\` with a real \`href\`, so browser link affordances all
    work: cmd/ctrl-click opens a new tab, shift-click a new window, middle-click a background tab,
    and \`target="_blank"\` opens a new tab. Pass \`rel\` explicitly when using \`target\` — \`target\` is
    an HTML attribute rather than an argument, so the component cannot inspect it to add
    \`rel="noopener noreferrer"\` for you.

    Routing behavior is delegated to \`LinkTo\` rather than reimplemented, with two deliberate
    departures:

    * \`role="button"\` is not applied in link mode, because announcing an anchor as a button
      breaks link semantics and Enter/Space expectations.
    * \`@disabled\` renders \`aria-disabled\` with no \`href\` instead of forwarding to \`LinkTo\`.
      \`LinkTo\` checks for modified clicks and \`target\` before it checks its own disabled state, so
      a disabled link would still navigate on cmd-click or with \`target="_blank"\`. An anchor
      without an \`href\` is neither focusable nor exposed as a link, which is the correct disabled
      semantic.

    Wraps the SPDS Core Container component.
  `,
  extends: [
    pick(BUTTON_ARGS, ['pill']),
    pick(COMPONENT_STATE_ARGS, [
      'autofocus',
      'disabled',
      { name: 'error', description: 'Apply error styling to the button' },
    ]),
    pick(CONTAINER_ARGS, [
      'column',
      'fitContent',
      'fitContentHeight',
      'fitContentWidth',
      'grid',
      'reverse',
      { name: 'inline', description: 'Display button inline' },
      { name: 'tag', default: TYPE_BUTTON },
    ]),
    pick(CONTENT_ARGS, ['iconOnly', { name: 'text', initialValue: 'Button' }]),
    LINK_ARGS,
    pick(POSITIONING_ARGS, ['absolute', 'fixed']),
    pickAll(ALL_LAYOUT_ARGS, {
      overrides: {
        alignItems: { default: CENTER },
        gap: { default: '8px' },
        justifyContent: { default: CENTER },
      },
    }),
  ],
  meta: {
    size: Arg.string({ default: NORMAL, description: 'Button size', options: BUTTON_SIZES }),
    type: Arg.string({
      default: TYPE_BUTTON,
      description: 'Button type attribute',
      options: HTML_BUTTON_TYPES,
    }),
  },
  name: 'Core Button',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=19170-11848&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/v/0/p/56566c-buttons',
  },
});

export default class SpdsCoreButton extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-button';
  styles = styles;

  get classesToMap() {
    return ['hasBorder', 'iconOnly', 'pill', 'size'];
  }

  @tracked baseElement;

  @service router;

  #warnedIncompleteModels = false;

  get alignItems() {
    return this.argOrDefault('alignItems');
  }

  get activeClass() {
    return this.argOrDefault('activeClass');
  }

  /**
   * `LinkTo` takes its route names as a space-separated string, so an array is joined for it.
   * Strings and booleans pass through untouched, keeping the space-separated form and the
   * force-on/force-off behavior available.
   * @return {string|boolean|undefined} the value to hand to `current-when`
   */
  get activeWhen() {
    let { activeWhen } = this.args;

    return Array.isArray(activeWhen) ? activeWhen.join(' ') : activeWhen;
  }

  /**
   * `disabled` is not a valid attribute on an anchor, so link mode communicates the state to
   * assistive technology instead.
   * @return {string|undefined} 'true' when a disabled link, otherwise undefined
   */
  get ariaDisabled() {
    return this.isLink && this.args.disabled ? 'true' : undefined;
  }

  /**
   * The `disabled` attribute is only emitted for elements that support it.
   * @return {boolean|undefined} the disabled arg outside link mode
   */
  get disabled() {
    return this.isLink ? undefined : this.args.disabled;
  }

  /**
   * Whether a model argument is supplied but cannot produce a usable URL.
   *
   * Collapsing a nullish model into an empty array would make `LinkTo`'s `isLoading` false, so
   * `href` would call `generateURL` with no models and throw for any route with a dynamic
   * segment. Forwarding it untouched instead yields a silent `href="#"`. Degrading to a
   * non-navigating element with a warning avoids both.
   * @return {boolean} true when a supplied model argument is incomplete
   */
  get hasIncompleteModels() {
    let incomplete = this.#detectIncompleteModels();

    runInDebug(() => {
      if (incomplete) this.#warnIncompleteModels();
    });

    return incomplete;
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  /**
   * Whether a usable route name is supplied. `isActive` counts an empty string as absent, and
   * both `isLink` and `route` read through this so they cannot disagree about what "supplied"
   * means — a disagreement would hand `LinkTo` an empty route name.
   * @return {boolean} true when `@route` holds a route name
   */
  get hasRoute() {
    return isActive(this.args.route);
  }

  get isButtonTag() {
    return this.tag === this.constructor.metadata.getDefault('tag');
  }

  /**
   * Whether the button renders as an anchor. Read from raw args rather than `argOrDefault`, since
   * a default on either routing argument would silently turn every button into a link.
   *
   * `isActive` only screens nullish and unset values, so it does not treat an empty object as
   * absent: an explicitly passed `@query={{hash}}` still makes this a link to the current route.
   * That is deliberate — deciding link-ness by whether the object has keys would let a button
   * change element type as its query params populate. Pass `@query` only when a link is intended.
   * @return {boolean} true when a routing argument is supplied
   */
  get isLink() {
    return this.hasRoute || isActive(this.args.query);
  }

  /**
   * Whether routing is delegated to `LinkTo`. Disabled links deliberately opt out — see the
   * component description for why forwarding `@disabled` to `LinkTo` is not sufficient.
   * @return {boolean} true when `LinkTo` should own the element
   */
  get isRouteLink() {
    return this.isLink && !this.args.disabled && !this.hasIncompleteModels;
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent');
  }

  /**
   * determines if role attribute should be set
   * @return {string|undefined} button role attribute value when not a button element
   */
  get role() {
    // Announcing an anchor as a button breaks link semantics and Enter/Space expectations.
    if (this.isLink) return undefined;

    return this.isButtonTag ? undefined : ROLE_BUTTON;
  }

  /**
   * The target route. A query-only link points at the route the user is already on, which covers
   * both an omitted `@route` and an empty one.
   * @return {string|undefined} the route name to link to
   */
  get route() {
    return this.hasRoute ? this.args.route : this.router.currentRouteName;
  }

  /**
   * Normalizes `@model`/`@models` into the array shape `LinkTo` uses internally. `LinkTo`'s own
   * `models` getter does `[this.named('model')]`, and that getter is the single path feeding
   * `href`, `transitionTo`, `isActiveForRoute`, and `isLoading` — so this is the same
   * normalization, one step earlier.
   *
   * Both branches key on argument presence, matching `#detectIncompleteModels`. Returning a
   * supplied `@models` untouched is safe because a value `LinkTo` could not use degrades to a
   * non-navigating element before it renders.
   * @return {Array} dynamic segment values in segment order
   */
  get routeModels() {
    let { model, models } = this.args;

    if ('models' in this.args) return models;
    if ('model' in this.args) return [model];

    return [];
  }

  get routeQuery() {
    return this.args.query ?? {};
  }

  get tag() {
    return this.isLink ? ANCHOR_TAG : this.argOrDefault('tag');
  }

  // if we're not using a button tag, we don't need a type attribute
  get type() {
    return this.isButtonTag ? this.argOrDefault('type') : undefined;
  }

  /**
   * determines the variant style of the button for data-test attributes
   * @return {string} button variant value
   */
  get variant() {
    if (this.link) return 'link';
    if (this.variantClass) return this.variantClass.split('-').at(-1);

    return this.tag;
  }

  /**
   * Keys on argument presence rather than value, matching `LinkTo`'s own `'model' in
   * args.named` check, so a metadata default can never stand in for an absent argument.
   *
   * A route that needs dynamic segments but was given no model argument at all is deliberately
   * not handled: there is nothing to inspect, and detecting it would require knowing the target
   * route's segments. `LinkTo` throws in that case, which is the right outcome for an authoring
   * error.
   * @return {boolean} true when a supplied model argument is incomplete
   */
  #detectIncompleteModels() {
    if ('models' in this.args) {
      let { models } = this.args;

      return !Array.isArray(models) || models.some(doesNotExist);
    }

    if ('model' in this.args) return doesNotExist(this.args.model);

    return false;
  }

  #warnIncompleteModels() {
    if (this.#warnedIncompleteModels) return;

    this.#warnedIncompleteModels = true;

    let { componentName } = this;
    let instanceId = this.id ? ` [instance: ${this.id}]` : '';

    Debug.warn(
      `${componentName} was given @route="${this.route}" with an incomplete model argument, so it ` +
        `rendered as a non-navigating element. Provide a value for every dynamic segment, or omit ` +
        `@route until the model is loaded${instanceId}`,
      false,
      { id: 'spds.route-link.incomplete-models' }
    );
  }

  <template>
    {{! `if` without an else yields undefined, which leaves the non-link path untouched }}
    {{#let
      (if
        this.isRouteLink
        (component
          LinkTo
          activeClass=this.activeClass
          current-when=this.activeWhen
          models=this.routeModels
          query=this.routeQuery
          replace=@replace
          route=this.route
        )
        (if this.isLink UnnavigableLink)
      )
      as |Wrapper|
    }}
      <Container
        @absolute={{@absolute}}
        @alignContent={{@alignContent}}
        @alignItems={{this.alignItems}}
        @alignSelf={{@alignSelf}}
        @area={{@area}}
        @areas={{@areas}}
        @autoFlow={{@autoFlow}}
        @column={{@column}}
        @columns={{@columns}}
        @content={{@text}}
        @error={{@error}}
        @fitContent={{@fitContent}}
        @fitContentHeight={{@fitContentHeight}}
        @fitContentWidth={{@fitContentWidth}}
        @fixed={{@fixed}}
        @flex={{@flex}}
        @flexWrap={{@flexWrap}}
        @gap={{this.gap}}
        @grid={{@grid}}
        @gridArea={{@gridArea}}
        @gridAutoColumns={{@gridAutoColumns}}
        @gridAutoFlow={{@gridAutoFlow}}
        @gridAutoRows={{@gridAutoRows}}
        @gridColumn={{@gridColumn}}
        @gridRow={{@gridRow}}
        @gridTemplateAreas={{@gridTemplateAreas}}
        @gridTemplateColumns={{@gridTemplateColumns}}
        @gridTemplateRows={{@gridTemplateRows}}
        @id={{this.id}}
        @inline={{@inline}}
        @justifyContent={{this.justifyContent}}
        @justifyItems={{@justifyItems}}
        @justifySelf={{@justifySelf}}
        @order={{@order}}
        @reverse={{@reverse}}
        @rows={{@rows}}
        @subgrid={{@subgrid}}
        @tag={{this.tag}}
        @wrapper={{Wrapper}}
        aria-disabled={{this.ariaDisabled}}
        class={{this.classes}}
        data-test-spds-button={{this.variant}}
        disabled={{this.disabled}}
        role={{this.role}}
        type={{this.type}}
        ...attributes
        {{SpdsAutofocus @autofocus}}
        {{this.setupComponent}}
        {{this.validateArguments}}
      >
        {{yield}}
      </Container>
    {{/let}}
  </template>
}

export const CORE_BUTTON_METADATA = SpdsCoreButton.metadata;
